import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as styles from './print-cover.module.scss';
import { arrow_expanded, avatar_not_found, pdc_print_cover, petronas_logo_2 } from '../../../../../assets/img';
import moment from 'moment';
import { AiCheckBox, BasicButton, BasicSelect } from '../../../../../assets/common';
import { debounce } from 'lodash';
import { Spin } from 'antd';
import { pdcMeetingApi } from '../../../../../services/pdcMeetingApi';
import { useSelector } from 'react-redux';
import { decodeBase64ToImage } from '../../../../../utils/helper';
import { APPROVING_AUTHORITY_KEYS, APPROVING_AUTHORITY_OPTIONS } from '../../../constants';

const PrintCover = ({ data, selectedEndorsed, setSelectedEndorsed, isErrorEndorsed, setIsErrorEndorsed, selectedType, setSelectedType }) => {
  const [value, setValue] = useState({
    endorser: null,
    approver: null,
  });
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({
    endorser: [],
    approver: [],
  });
  const roleActive = useSelector((state) => state.user.roleActive);
  const [displaySelectedKeys, setDisplaySelectedKeys] = useState([]);
  const [open, setOpen] = useState(false);

  const year = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return currentYear;
  }, [data]);

  const handleClickChange = () => {
    setSelectedEndorsed({
      endorser: null,
      approver: null,
    });
  };

  const fetchData = async (key, keyword) => {
    setLoading(true);
    try {
      const permission = {
        role: roleActive.roleId,
      };
      const params = { search: keyword, limit: 4, page: 1 };
      const res = await pdcMeetingApi.getSearchStaff(params, permission);
      if (res.status === 200) {
        setOptions((prev) => ({ ...prev, [key]: res?.data?.result?.profiles || [] }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setOptions((prev) => ({ ...prev, [key]: [] }));
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchData = debounce(fetchData, 500);

  const handleSearch = (key, value) => {
    if (value) {
      debouncedFetchData(key, value);
    }
  };

  const handleClickSelect = async () => {
    const hasError = selectedType.includes('all') ? !value?.endorser || !value?.approver : selectedType.some((key) => !value?.[key]);
    if (hasError) {
      setIsErrorEndorsed((prev) => {
        const nextErrors = { ...prev };
        Object.keys(nextErrors).forEach((roleKey) => {
          nextErrors[roleKey] = selectedType.includes('all') || selectedType.includes(roleKey) ? !selectedEndorsed?.[roleKey] : false;
        });
        return nextErrors;
      });
      return;
    }

    setSelectedEndorsed(value);
    setIsErrorEndorsed({
      endorser: false,
      approver: false,
    });
    setValue({
      endorser: null,
      approver: null,
    });
    try {
      await pdcMeetingApi.saveEndorsedBy(data.meeting_pdc_id, {
        endorser: value.endorser?.staffId,
        approver: value.approver?.staffId,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelect = (key, value) => {
    const item = options[key].find((item) => item.staffId === value);
    setValue((prev) => ({ ...prev, [key]: item }));
  };

  useEffect(() => {
    const endorsedBy = data?.endorsed_by;
    const approverBy = data?.approver_by;

    if (endorsedBy && endorsedBy?.staff_id) {
      setSelectedEndorsed((prev) => ({
        ...prev,
        endorser: { staffId: endorsedBy.staff_id, positionName: endorsedBy.position_name, birthName: endorsedBy.birth_name },
      }));
    }

    if (approverBy && approverBy?.staff_id) {
      setSelectedEndorsed((prev) => ({
        ...prev,
        approver: { staffId: approverBy.staff_id, positionName: approverBy.position_name, birthName: approverBy.birth_name },
      }));
    }
  }, [data]);

  const disabledSelect = useMemo(() => {
    return !selectedType.length || selectedType.includes('all') ? !value?.endorser || !value?.approver : selectedType.some((key) => !value?.[key]);
  }, [selectedType, value]);

  const handleSelectedKeys = (value) => {
    let keys = [];

    if (value === APPROVING_AUTHORITY_KEYS.ALL) {
      keys = APPROVING_AUTHORITY_OPTIONS.map((option) => option.value);

      if (displaySelectedKeys.length === APPROVING_AUTHORITY_OPTIONS.length) {
        keys = [];
      }
    }

    if (value !== APPROVING_AUTHORITY_KEYS.ALL) {
      keys = displaySelectedKeys.filter((key) => key !== APPROVING_AUTHORITY_KEYS.ALL && key !== value);

      if (!displaySelectedKeys.includes(value)) {
        keys = [...keys, value];

        if (keys.length === APPROVING_AUTHORITY_OPTIONS.length - 1) {
          keys = [...keys, APPROVING_AUTHORITY_KEYS.ALL];
        }
      }
    }

    setDisplaySelectedKeys(keys);
  };

  const handleClickApply = () => {
    setSelectedType(displaySelectedKeys);
    setOpen(false);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.left}>
        <div className={styles.name}>
          <div>PROPOSALS</div>
          <div>{data?.meeting_name}</div>
        </div>
        <div className={styles.date}>
          <div>{data?.date && moment(data?.date).format('DD.MM.YYYY')}</div>
        </div>
        <div className={styles.desc1}>
          The PETRONAS Group adopts zero tolerance against all forms of bribery and corruption. We abide by the PETRONAS Code Of Conduct and Business
          Ethics (CoBE) & Anti-Bribery and Corruption (ABC) Manual, guided by our Shared Values and Statement of Purpose.
        </div>
        <div className={styles.desc2}>
          <div>©{year} Petroliam Nasional Berhard (PETRONAS)</div>
          <div>
            All rights reserved. No part of this document may be reproduced in any possible, stored in a retrieval system, transmitted and/or
            disseminated in any form or by any means (digital, mechanical, hard copy, recording or otherwise) without the permission of the copyright
            owner.
          </div>
        </div>
      </div>
      <div className={styles.right}>
        {Object.keys(selectedEndorsed || {}).some((key) => selectedEndorsed[key]) ? (
          Object.entries(selectedEndorsed || {}).map(([key, value]) => {
            if (!value) return null;

            return (
              <div className={styles.endorsed} key={key}>
                <div style={{ textTransform: 'capitalize' }}>{`${key.slice(0, -1)}d by`}</div>
                <div />
                <div>
                  <div className={styles.positionWidth}>{value?.birthName || 'N/A'}</div>
                  <span className={styles.changeBtn} onClick={handleClickChange}>
                    Change
                  </span>
                </div>
                <div className={styles.positionWidth}>{value?.positionName || 'N/A'}</div>
              </div>
            );
          })
        ) : (
          <Spin spinning={loading}>
            <div className={styles.selection}>
              <div onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
                Approving Authority
                <img className={styles.collapsed} src={arrow_expanded} alt="collapsed" />
              </div>
              <div className={styles.dropdown} style={open ? { zIndex: 10, opacity: 1 } : { zIndex: -10, opacity: 0 }}>
                <div className={styles.list}>
                  {APPROVING_AUTHORITY_OPTIONS.map(({ value, label }) => (
                    <div className={styles.row} key={value}>
                      <AiCheckBox
                        value={value}
                        checked={displaySelectedKeys.includes(value)}
                        onChange={(e) => {
                          handleSelectedKeys(e.target.value);
                        }}
                      />{' '}
                      {label}
                    </div>
                  ))}
                </div>
                <div className={styles.btnPreview}>
                  <BasicButton mode="teal" type="button" onClick={handleClickApply}>
                    Apply
                  </BasicButton>
                </div>
              </div>
              {Object.entries(selectedEndorsed || {}).map(([key]) => {
                if (!selectedType.includes('all') && !selectedType.includes(key)) return null;

                const currentStaff = value?.[key];
                const hasError = isErrorEndorsed[key] && !currentStaff?.birthName;
                const currentOptions = options[key] || [];

                return (
                  <div key={key} className={styles.selectBox}>
                    <div style={{ textTransform: 'capitalize' }}>Select {key}</div>
                    <BasicSelect
                      showSearch
                      placeholder="Search By Staff Name/ Staff ID"
                      optionLabelProp="label"
                      value={currentStaff?.birthName || currentStaff?.positionName || null}
                      filterOption={false}
                      onSearch={(searchText) => handleSearch(key, searchText)}
                      onSelect={(selected) => handleSelect(key, selected)}
                      getPopupContainer={(trigger) => trigger}
                      virtual={false}
                      error={hasError}
                    >
                      {currentOptions.map((item) => (
                        <BasicSelect.Option key={item.staffId} value={item.staffId}>
                          <div className={styles.option}>
                            <div className={styles.avatar}>
                              <img alt="avatar" src={item.image ? decodeBase64ToImage(item.image) : avatar_not_found} />
                            </div>
                            <div className={styles.optionInfo}>
                              <div>{item.birthName || 'N/A'}</div>
                              <div>{item.positionName || 'N/A'}</div>
                            </div>
                          </div>
                        </BasicSelect.Option>
                      ))}
                    </BasicSelect>
                    {hasError && <div className={styles.requireField}>Required field</div>}
                  </div>
                );
              })}
              <BasicButton disabled={disabledSelect} mode="teal" className="mt-4" onClick={handleClickSelect}>
                Select
              </BasicButton>
            </div>
          </Spin>
        )}
      </div>
      <img className={styles.cover} src={pdc_print_cover} alt="print-cover" />
      <img className={styles.logo} src={petronas_logo_2} alt="petronas_logo" />
    </div>
  );
};

export default PrintCover;

PrintCover.propTypes = {
  data: PropTypes.object,
};
